import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Collapse,
  CircularProgress,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  SaveAlt as SaveIcon,
  PersonOutline as PersonIcon,
  ShoppingBag as OrderIcon,
  Favorite as FavoriteIcon,
  History as HistoryIcon,
  LocationOn as LocationIcon,
  VpnKey as PasswordIcon,
  ArrowForward as ArrowIcon,
  Event as EventIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import PayPalButtonStandard from '../components/PayPalButtonStandard';
import { useAuth } from '../contexts/AuthContext';
import {
  getFirestore, doc, getDoc, collection, query, where,
  orderBy, getDocs, updateDoc, setDoc, deleteDoc, arrayUnion, arrayRemove, serverTimestamp
} from 'firebase/firestore';
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

// 选项卡面板组件
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// 个人资料组件
const ProfilePanel = ({ user, userData }) => {
  const { updateUserProfile } = useAuth();  // 从AuthContext获取updateUserProfile函数
  const [formData, setFormData] = useState({
    displayName: userData?.displayName || user.displayName || '',
    phoneNumber: userData?.phoneNumber || '',
    email: user.email || '',
    bio: userData?.bio || ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 使用AuthContext中的updateUserProfile函数
      await updateUserProfile({
        displayName: formData.displayName,
        phoneNumber: formData.phoneNumber,
        bio: formData.bio
      });

      setSuccess('个人资料已更新');
    } catch (err) {
      console.error('更新个人资料失败:', err);
      setError('更新个人资料失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Avatar 
          src={user?.photoURL || "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/user-placeholder.png"} 
          alt={formData.displayName}
          sx={{ width: 100, height: 100, mr: 3 }}
        />
        <Box>
          <Typography variant="h6">{formData.displayName || '用户'}</Typography>
          <Typography variant="body2" color="textSecondary">
            {userData?.createdAt ? `注册于 ${new Date(userData.createdAt.toDate()).toLocaleDateString()}` : ''}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="姓名"
            name="displayName"
            value={formData.displayName}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="手机号码"
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="电子邮箱"
            name="email"
            value={formData.email}
            disabled
            helperText="电子邮箱不可修改"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="个人简介"
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            multiline
            rows={4}
          />
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

      <Button 
        type="submit" 
        variant="contained" 
        color="primary" 
        sx={{ mt: 3 }} 
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : '保存修改'}
      </Button>
    </Box>
  );
};

// 历史订单组件
const OrdersPanel = ({ userId }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const db = getFirestore();
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));

        setOrders(ordersData);
      } catch (err) {
        console.error('获取订单失败:', err);
        setError('获取订单数据失败');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [userId]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (orders.length === 0) {
    return <Alert severity="info">暂无订单记录</Alert>;
  }

  return (
    <Box>
      {orders.map(order => (
        <Paper key={order.id} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1">订单号: {order.id}</Typography>
            <Typography color="primary" fontWeight="bold">
              {order.status === 'COMPLETED' && '已完成'}
              {order.status === 'CREATED' && '待付款'}
              {order.status === 'PROCESSING' && '处理中'}
              {order.status === 'SHIPPED' && '已发货'}
              {order.status === 'CANCELLED' && '已取消'}
            </Typography>
          </Box>

          <Typography variant="body2" color="textSecondary">
            下单时间: {new Date(order.createdAt).toLocaleString()}
          </Typography>

          <Divider sx={{ my: 1 }} />

          {order.items && order.items.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', my: 1 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography>{item.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  数量: {item.quantity}
                </Typography>
              </Box>
              <Typography>S${parseFloat(item.price).toFixed(2)}</Typography>
            </Box>
          ))}

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>总计:</Typography>
            <Typography fontWeight="bold">
              S${order.amount?.total ? parseFloat(order.amount.total).toFixed(2) : '0.00'}
            </Typography>
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => navigate(`/order/${order.id}`)}
            >
              查看详情
            </Button>
            {order.status === 'CREATED' && (
              <Button 
                variant="contained" 
                color="primary"
                size="small"
                onClick={() => navigate('/checkout', { state: { orderId: order.id } })}
              >
                立即付款
              </Button>
            )}
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// 收藏商品组件
const FavoritesPanel = ({ userId }) => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const db = getFirestore();

        // 获取用户收藏列表
        const userDoc = await getDoc(doc(db, 'users', userId));
        const favoriteIds = userDoc.data()?.favoriteProducts || [];

        if (favoriteIds.length === 0) {
          setFavorites([]);
          setLoading(false);
          return;
        }

        // 获取收藏商品详情
        const productsPromises = favoriteIds.map(id =>
          getDoc(doc(db, 'products', id))
        );

        const productDocs = await Promise.all(productsPromises);
        const productData = productDocs.map(doc => {
          if (doc.exists()) {
            return { id: doc.id, ...doc.data() };
          }
          return null;
        }).filter(product => product !== null);

        setFavorites(productData);
      } catch (err) {
        console.error('获取收藏商品失败:', err);
        setError('获取收藏商品失败');
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [userId]);

  const removeFavorite = async (productId) => {
    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userId);

      // 从用户收藏列表中移除
      await updateDoc(userRef, {
        favoriteProducts: arrayRemove(productId)
      });

      // 更新本地状态
      setFavorites(prev => prev.filter(product => product.id !== productId));
    } catch (err) {
      console.error('移除收藏失败:', err);
      setError('移除收藏失败: ' + err.message);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (favorites.length === 0) {
    return <Alert severity="info">暂无收藏商品</Alert>;
  }

  return (
    <Grid container spacing={2}>
      {favorites.map(product => (
        <Grid item xs={12} sm={6} md={4} key={product.id}>
          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <img 
              src={product.imageUrl || product.images?.[0] || "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/product-placeholder.png"} 
              alt={product.name}
              style={{ width: '100%', height: 180, objectFit: 'cover', marginBottom: 12 }}
            />

            <Typography variant="h6" gutterBottom>{product.name}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {product.description?.substring(0, 80)}...
            </Typography>

            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography color="primary" fontWeight="bold">
                S${parseFloat(product.price).toFixed(2)}
              </Typography>

              <Box>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ mr: 1 }}
                  onClick={() => removeFavorite(product.id)}
                >
                  取消收藏
                </Button>
                <Button 
                  variant="contained"
                  size="small"
                  onClick={() => window.location.href = `/product/${product.id}`}
                >
                  查看
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

// 算命记录组件
const FortuneRecordsPanel = ({ userId }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFortuneRecords = async () => {
      try {
        const db = getFirestore();
        const recordsRef = collection(db, 'fortuneReadings');
        const q = query(
          recordsRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const recordsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date()
        }));

        setRecords(recordsData);
      } catch (err) {
        console.error('获取算命记录失败:', err);
        setError('获取算命记录失败');
      } finally {
        setLoading(false);
      }
    };

    fetchFortuneRecords();
  }, [userId]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (records.length === 0) {
    return <Alert severity="info">暂无算命记录</Alert>;
  }

  return (
    <Box>
      {records.map(record => (
        <Paper key={record.id} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1">
              八字: {record.input?.year} {record.input?.month} {record.input?.day} {record.input?.hour}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {new Date(record.createdAt).toLocaleString()}
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ maxHeight: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Typography variant="body2">
              {record.result?.substring(0, 300)}...
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => window.location.href = `/ai-fortune/record/${record.id}`}
            >
              查看详情
            </Button>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};

// 收货地址组件
const AddressesPanel = ({ userId, userData }) => {
  const [addresses, setAddresses] = useState(userData?.addresses || []);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 只加载用户现有的收货地址，不自动添加结账时保存的地址
  useEffect(() => {
    const loadAddresses = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists()) {
          // 只获取用户已保存的地址列表，不自动添加结账时保存的地址
          const userAddresses = userDoc.data().addresses || [];
          setAddresses(userAddresses);
        }
      } catch (err) {
        console.error('加载地址失败:', err);
        setError('加载地址失败: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadAddresses();
  }, [userId]);

  // 初始化空地址表单
  const emptyAddressForm = {
    id: '',
    name: '',
    province: '',
    city: '',
    district: '',
    address: '',
    phone: '',
    postalCode: '',
    isDefault: false
  };

  const [formData, setFormData] = useState(emptyAddressForm);

  // 处理表单输入变化
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'isDefault' ? checked : value
    });
  };

  // 编辑地址
  const handleEdit = (address) => {
    setFormData(address);
    setCurrentAddress(address);
    setIsEditing(true);
  };

  // 添加新地址
  const handleAddNew = () => {
    setFormData({
      ...emptyAddressForm,
      id: `addr_${Date.now()}`
    });
    setCurrentAddress(null);
    setIsEditing(true);
  };

  // 取消编辑
  const handleCancel = () => {
    setIsEditing(false);
    setFormData(emptyAddressForm);
    setCurrentAddress(null);
  };

  // 删除地址
  const handleDelete = async (addressId) => {
    if (!window.confirm('确定要删除此地址吗？')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const db = getFirestore();
      const userRef = doc(db, 'users', userId);

      // 过滤掉要删除的地址
      const updatedAddresses = addresses.filter(addr => addr.id !== addressId);

      await updateDoc(userRef, {
        addresses: updatedAddresses
      });
      
      // 单独更新时间戳
      await updateDoc(userRef, {
        updatedAt: serverTimestamp()
      });

      setAddresses(updatedAddresses);
      setSuccess('地址已删除');
    } catch (err) {
      console.error('删除地址失败:', err);
      setError('删除地址失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 保存地址
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 表单验证
      const requiredFields = ['name', 'province', 'city', 'district', 'address', 'phone'];
      for (const field of requiredFields) {
        if (!formData[field]) {
          throw new Error(`${field === 'name' ? '收货人' : field === 'phone' ? '联系电话' : '地址'}不能为空`);
        }
      }

      const db = getFirestore();
      const userRef = doc(db, 'users', userId);
      
      // 如果是添加新地址，检查是否已存在相似地址
      if (!currentAddress) {
        // 检查是否已存在相似的地址
        const duplicateAddress = addresses.find(addr => {
          // 电话和地址都完全匹配
          if (addr.phone === formData.phone && addr.address === formData.address) {
            return true;
          }
          
          // 电话匹配并且地区和地址部分匹配
          if (addr.phone === formData.phone && 
              addr.province === formData.province && 
              addr.city === formData.city &&
              (addr.address.includes(formData.address) || formData.address.includes(addr.address))) {
            return true;
          }
          
          return false;
        });
        
        if (duplicateAddress) {
          throw new Error('该地址已存在，无需重复添加');
        }
      }

      let updatedAddresses;

      if (currentAddress) {
        // 更新现有地址
        updatedAddresses = addresses.map(addr =>
          addr.id === formData.id ? formData : addr
        );
      } else {
        // 添加新地址
        updatedAddresses = [...addresses, formData];
      }

      // 如果设置为默认地址，更新其他地址的默认状态
      if (formData.isDefault) {
        updatedAddresses = updatedAddresses.map(addr => ({
          ...addr,
          isDefault: addr.id === formData.id
        }));
      }

      // 先添加/更新地址
      await updateDoc(userRef, {
        addresses: updatedAddresses
      });
      
      // 单独更新时间戳
      await updateDoc(userRef, {
        updatedAt: serverTimestamp()
      });

      setAddresses(updatedAddresses);
      setIsEditing(false);
      setFormData(emptyAddressForm);
      setCurrentAddress(null);
      setSuccess(currentAddress ? '地址已更新' : '地址已添加');
    } catch (err) {
      console.error('保存地址失败:', err);
      setError('保存地址失败: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {!isEditing ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleAddNew}
            >
              添加新地址
            </Button>
          </Box>

          {addresses.length === 0 ? (
            <Alert severity="info">暂无收货地址</Alert>
          ) : (
            <Grid container spacing={2}>
              {addresses.map(address => (
                <Grid item xs={12} sm={6} key={address.id}>
                  <Paper sx={{ p: 2, position: 'relative' }}>
                    {address.isDefault && (
                      <Box 
                        sx={{ 
                          position: 'absolute', 
                          top: 10, 
                          right: 10, 
                          bgcolor: 'primary.main',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '0.75rem'
                        }}
                      >
                        默认
                      </Box>
                    )}

                    <Typography variant="subtitle1">{address.name}</Typography>
                    <Typography variant="body2">{address.phone}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {address.province} {address.city} {address.district} {address.address}
                    </Typography>
                    {address.postalCode && (
                      <Typography variant="body2" color="textSecondary">
                        邮政编码: {address.postalCode}
                      </Typography>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button 
                        variant="outlined" 
                        size="small" 
                        sx={{ mr: 1 }}
                        onClick={() => handleEdit(address)}
                      >
                        编辑
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="error"
                        size="small"
                        onClick={() => handleDelete(address.id)}
                        disabled={address.isDefault}
                      >
                        删除
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom>
            {currentAddress ? '编辑地址' : '添加新地址'}
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="收货人"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="联系电话"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="省份/国家"
                name="province"
                value={formData.province}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="城市/地区"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="区/县"
                name="district"
                value={formData.district}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="邮政编码"
                name="postalCode"
                value={formData.postalCode || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="详细地址"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDefault}
                    onChange={handleChange}
                    name="isDefault"
                  />
                }
                label="设为默认地址"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={handleCancel} 
              sx={{ mr: 1 }}
              disabled={loading}
            >
              取消
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : '保存'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// 密码管理组件
const PasswordPanel = ({ user }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // 表单验证
      if (formData.newPassword !== formData.confirmPassword) {
        throw new Error('新密码与确认密码不匹配');
      }

      if (formData.newPassword.length < 6) {
        throw new Error('新密码长度不能少于6个字符');
      }

      // 重新认证用户
      const auth = getAuth();
      const credential = EmailAuthProvider.credential(
        user.email,
        formData.currentPassword
      );

      await reauthenticateWithCredential(user, credential);

      // 更新密码
      await updatePassword(user, formData.newPassword);

      // 清空表单
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setSuccess('密码已成功更新');
    } catch (err) {
      console.error('更新密码失败:', err);
      if (err.code === 'auth/wrong-password') {
        setError('当前密码不正确');
      } else if (err.code === 'auth/too-many-requests') {
        setError('尝试次数过多，请稍后再试');
      } else {
        setError('更新密码失败: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // 仅对邮箱登录用户显示密码修改选项
  if (user.providerData && user.providerData[0]?.providerId !== 'password') {
    return (
      <Alert severity="info">
        您使用第三方账号登录，无法修改密码。如需设置密码，请使用"忘记密码"功能。
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" gutterBottom>修改密码</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="password"
            label="当前密码"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="password"
            label="新密码"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            required
            helperText="密码长度不能少于6个字符"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            type="password"
            label="确认新密码"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
        </Grid>
      </Grid>

      <Button 
        type="submit" 
        variant="contained" 
        color="primary" 
        sx={{ mt: 3 }} 
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : '更新密码'}
      </Button>
    </Box>
  );
};

// 占卜师预约记录组件
const BookingRecordsPanel = ({ userId }) => {
  const [bookings, setBookings] = useState([]);
  const [fortuneTellers, setFortuneTellers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // 添加支付相关状态
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  
  // 分类预约
  const [unpaidBookings, setUnpaidBookings] = useState([]);
  const [otherBookings, setOtherBookings] = useState([]);

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const db = getFirestore();
        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('userId', '==', userId),
          orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        const bookingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date()
        }));

        setBookings(bookingsData);
        
        // 分类预约：未支付的和其他状态
        const unpaid = bookingsData.filter(booking => 
          booking.status === 'pending' && booking.paymentStatus === 'pending'
        );
        const others = bookingsData.filter(booking => 
          !(booking.status === 'pending' && booking.paymentStatus === 'pending')
        );
        
        setUnpaidBookings(unpaid);
        setOtherBookings(others);

        // 获取命理师信息
        const fortuneTellerIds = [...new Set(bookingsData.map(booking => booking.fortuneTellerId))];

        if (fortuneTellerIds.length > 0) {
          const fortuneTellerData = {};

          for (const id of fortuneTellerIds) {
            const ftDoc = await getDoc(doc(db, 'fortuneTellers', id));
            if (ftDoc.exists()) {
              fortuneTellerData[id] = {
                id: ftDoc.id,
                ...ftDoc.data()
              };
            }
          }

          setFortuneTellers(fortuneTellerData);
        }
      } catch (err) {
        console.error('获取预约记录失败:', err);
        setError('获取预约记录失败');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [userId]);

  // 处理支付按钮点击
  const handlePaymentClick = (booking) => {
    setSelectedBooking(booking);
    setPaymentDialogOpen(true);
    setPaymentSuccess(false);
    setPaymentError('');
  };
  
  // 处理支付成功
  const handlePaymentSuccess = async (paymentDetails) => {
    try {
      console.log('支付成功:', paymentDetails);
      
      if (selectedBooking) {
        const db = getFirestore();
        const bookingRef = doc(db, 'bookings', selectedBooking.id);
        
        // 更新预约状态为已支付/已确认
        await updateDoc(bookingRef, {
          status: 'confirmed',
          paymentStatus: 'completed',
          paymentDetails: paymentDetails,
          updatedAt: serverTimestamp()
        });
        
        // 更新本地状态
        const updatedBooking = {
          ...selectedBooking, 
          status: 'confirmed', 
          paymentStatus: 'completed'
        };
        
        setBookings(prev => prev.map(booking => 
          booking.id === selectedBooking.id ? updatedBooking : booking
        ));
        
        // 更新分类列表
        setUnpaidBookings(prev => prev.filter(booking => booking.id !== selectedBooking.id));
        setOtherBookings(prev => [updatedBooking, ...prev]);
        
        setPaymentSuccess(true);
      }
    } catch (error) {
      console.error('更新支付状态失败:', error);
      setPaymentError(`更新支付状态失败: ${error.message}`);
    }
  };
  
  // 处理支付错误
  const handlePaymentError = (error) => {
    console.error('支付失败:', error);
    setPaymentError(`支付处理出错: ${error.message || '未知错误'}`);
  };
  
  // 关闭支付对话框
  const handleClosePaymentDialog = () => {
    if (paymentSuccess) {
      setPaymentDialogOpen(false);
      setSelectedBooking(null);
    } else {
      setPaymentDialogOpen(false);
    }
  };
  
  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('确定要取消此预约吗？')) {
      return;
    }

    try {
      const db = getFirestore();
      const bookingRef = doc(db, 'bookings', bookingId);

      await updateDoc(bookingRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      });

      // 更新本地状态
      const updatedBooking = bookings.find(b => b.id === bookingId);
      if (updatedBooking) {
        const cancelledBooking = { ...updatedBooking, status: 'cancelled' };
        
        setBookings(prev => prev.map(booking =>
          booking.id === bookingId ? cancelledBooking : booking
        ));
        
        // 更新分类列表
        if (updatedBooking.status === 'pending' && updatedBooking.paymentStatus === 'pending') {
          setUnpaidBookings(prev => prev.filter(booking => booking.id !== bookingId));
          setOtherBookings(prev => [cancelledBooking, ...prev]);
        } else {
          setOtherBookings(prev => prev.map(booking =>
            booking.id === bookingId ? cancelledBooking : booking
          ));
        }
      }
    } catch (err) {
      console.error('取消预约失败:', err);
      setError('取消预约失败: ' + err.message);
    }
  };

  const getStatusText = (status, paymentStatus) => {
    if (status === 'pending' && paymentStatus === 'pending') {
      return '待支付';
    }
    
    switch (status) {
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'completed': return '已完成';
      case 'cancelled': return '已取消';
      default: return '未知';
    }
  };

  const formatTimeSlot = (timeSlot) => {
    if (!timeSlot) return '';
    if (Array.isArray(timeSlot)) {
      return timeSlot.join(', ');
    }
    return timeSlot;
  };

  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (bookings.length === 0) {
    return <Alert severity="info">暂无预约记录</Alert>;
  }

  return (
    <Box>
      {/* 未支付预约区域 */}
      {unpaidBookings.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            bgcolor: 'warning.light', 
            p: 2, 
            borderRadius: 1,
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="h6" fontWeight="bold">
              待支付预约
            </Typography>
            <Typography variant="body2" color="text.secondary">
              以下预约需要支付才能确认
            </Typography>
          </Box>
          
          {unpaidBookings.map(booking => (
            <Paper key={booking.id} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'warning.main' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1">
                  {fortuneTellers[booking.fortuneTellerId]?.name || '未知命理师'}
                </Typography>
                <Box>
                  <Chip 
                    label={getStatusText(booking.status, booking.paymentStatus)} 
                    color="warning"
                    size="small"
                  />
                </Box>
          </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar 
                  src={fortuneTellers[booking.fortuneTellerId]?.photoURL || "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/user-placeholder.png"} 
                  alt={fortuneTellers[booking.fortuneTellerId]?.name || '未知命理师'}
                  sx={{ width: 50, height: 50, mr: 2 }}
                />
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {fortuneTellers[booking.fortuneTellerId]?.title || ''}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {fortuneTellers[booking.fortuneTellerId]?.specialties?.join(', ') || ''}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    预约日期：{booking.date.toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">
                    预约时间：{booking.timeSlots 
                              ? formatTimeSlot(booking.timeSlots) 
                              : formatTimeSlot(booking.timeSlot)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" fontWeight="bold">
                    总价：S${booking.price?.toFixed(2) || '0.00'}
                  </Typography>
                </Grid>
                {booking.topic && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      咨询主题：{booking.topic}
                    </Typography>
                  </Grid>
                )}
                {booking.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      备注：{booking.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="textSecondary">
                  预约编号：{booking.id}
                </Typography>
                <Box>
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="small"
                    sx={{ mr: 1 }}
                    startIcon={<PaymentIcon />}
                    onClick={() => handlePaymentClick(booking)}
                  >
                    立即支付
                  </Button>
                  <Button 
                    variant="outlined" 
                    color="error"
                    size="small"
                    onClick={() => handleCancelBooking(booking.id)}
                  >
                    取消预约
                  </Button>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
      
      {/* 其他预约区域 */}
      <Box>
        {otherBookings.length > 0 ? (
          <>
            <Typography variant="h6" gutterBottom>
              预约记录
            </Typography>
            
            {otherBookings.map(booking => (
              <Paper key={booking.id} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1">
                    {fortuneTellers[booking.fortuneTellerId]?.name || '未知命理师'}
                  </Typography>
                  <Box>
                    <Chip 
                      label={getStatusText(booking.status, booking.paymentStatus)} 
                      color={
                        booking.status === 'confirmed' ? 'primary' : 
                        booking.status === 'completed' ? 'success' : 
                        booking.status === 'cancelled' ? 'error' : 
                        'default'
                      }
                      size="small"
                    />
                  </Box>
                </Box>
  
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar 
                    src={fortuneTellers[booking.fortuneTellerId]?.photoURL || "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/user-placeholder.png"} 
                    alt={fortuneTellers[booking.fortuneTellerId]?.name || '未知命理师'}
                    sx={{ width: 50, height: 50, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {fortuneTellers[booking.fortuneTellerId]?.title || ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {fortuneTellers[booking.fortuneTellerId]?.specialties?.join(', ') || ''}
                    </Typography>
                  </Box>
                </Box>
  
                <Divider sx={{ my: 1 }} />
  
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      预约日期：{booking.date.toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      预约时间：{Array.isArray(booking.timeSlots) 
                                ? booking.timeSlots.join(', ') 
                                : formatTimeSlot(booking.timeSlot)}
                    </Typography>
                  </Grid>
                  {booking.topic && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        咨询主题：{booking.topic}
                      </Typography>
                    </Grid>
                  )}
                  {booking.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        备注：{booking.notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
  
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="textSecondary">
                    预约编号：{booking.id}
                  </Typography>
                  {(booking.status === 'pending' || booking.status === 'confirmed') && (
                    <Button 
                      variant="outlined" 
                      color="error"
                      size="small"
                      onClick={() => handleCancelBooking(booking.id)}
                    >
                      取消预约
                    </Button>
                  )}
                </Box>
              </Paper>
            ))}
          </>
        ) : unpaidBookings.length === 0 && (
          <Alert severity="info">暂无预约记录</Alert>
        )}
      </Box>
      
      {/* 支付对话框 */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={handleClosePaymentDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {paymentSuccess 
            ? "支付成功" 
            : `支付预约 - ${fortuneTellers[selectedBooking?.fortuneTellerId]?.name || '占卜师'} 的服务`}
        </DialogTitle>
        <DialogContent>
          {paymentSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>支付已成功处理</Alert>
              <Typography variant="body1" gutterBottom>
                您的预约已确认，命理师将在预约时间与您联系。
              </Typography>
              <Typography variant="body2" color="textSecondary">
                您可以在"预约记录"中查看此预约的最新状态。
              </Typography>
            </Box>
          ) : selectedBooking ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  预约详情:
                </Typography>
                <Typography variant="body2">
                  预约编号: {selectedBooking.id}
                </Typography>
                <Typography variant="body2">
                  命理师: {fortuneTellers[selectedBooking.fortuneTellerId]?.name || '未知命理师'}
                </Typography>
                <Typography variant="body2">
                  日期: {selectedBooking.date.toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  时间: {selectedBooking.timeSlots 
                    ? formatTimeSlot(selectedBooking.timeSlots) 
                    : formatTimeSlot(selectedBooking.timeSlot)}
                </Typography>
                <Typography variant="body2">
                  时长: {selectedBooking.timeSlots 
                    ? `${selectedBooking.timeSlots.length * 30}分钟` 
                    : "30分钟"}
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                  应付金额: S${selectedBooking.price?.toFixed(2) || '0.00'}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {paymentError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {paymentError}
                </Alert>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom textAlign="center">
                  选择支付方式
                </Typography>
                <PayPalButtonStandard 
                  amount={selectedBooking.price} 
                  currency="SGD"
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              </Box>
            </Box>
          ) : (
            <Typography>加载中...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>
            {paymentSuccess ? "完成" : "关闭"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const UserPage = () => {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 处理标签切换
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  
  // 处理特殊标签点击
  const handleTabSpecialClick = (tabName) => {
    if (tabName === 'orders') {
      navigate('/user/orders');
    } else if (tabName === 'bookings') {
      navigate('/user/bookings');
    }
  };

  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">请先登录以访问个人中心</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>个人中心</Typography>

      <Paper sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons="auto">
            <Tab label="个人资料" icon={<PersonIcon />} iconPosition="start" />
            <Tab 
              label="历史订单" 
              icon={<OrderIcon />} 
              iconPosition="start" 
              onClick={() => handleTabSpecialClick('orders')}
            />
            <Tab label="收藏商品" icon={<FavoriteIcon />} iconPosition="start" />
            <Tab label="算命记录" icon={<HistoryIcon />} iconPosition="start" />
            <Tab label="收货地址" icon={<LocationIcon />} iconPosition="start" />
            <Tab label="密码管理" icon={<PasswordIcon />} iconPosition="start" />
            <Tab 
              label="预约记录" 
              icon={<EventIcon />} 
              iconPosition="start"
              onClick={() => handleTabSpecialClick('bookings')}
            />
          </Tabs>
        </Box>

        {/* 个人资料面板 */}
        <TabPanel value={value} index={0}>
          <ProfilePanel user={currentUser} userData={userData} />
        </TabPanel>

        {/* 历史订单面板 */}
        <TabPanel value={value} index={1}>
          <OrdersPanel userId={currentUser.uid} />
        </TabPanel>

        {/* 收藏商品面板 */}
        <TabPanel value={value} index={2}>
          <FavoritesPanel userId={currentUser.uid} />
        </TabPanel>

        {/* 算命记录面板 */}
        <TabPanel value={value} index={3}>
          <FortuneRecordsPanel userId={currentUser.uid} />
        </TabPanel>

        {/* 收货地址面板 */}
        <TabPanel value={value} index={4}>
          <AddressesPanel userId={currentUser.uid} userData={userData} />
        </TabPanel>

        {/* 密码管理面板 */}
        <TabPanel value={value} index={5}>
          <PasswordPanel user={currentUser} />
        </TabPanel>

        {/* 预约记录面板 */}
        <TabPanel value={value} index={6}>
          <BookingRecordsPanel userId={currentUser.uid} />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default UserPage;